import { useState } from "react";
import { useEffect } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { alpha, useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import constants from "../../assets/constants";
import { createNewUser } from "../../data/omniDataModel";
import * as PatientSessionActions from "../../state/redux/patientSession";
import * as PracticeSessionActions from "../../state/redux/practiceSession";
import { capitalizeAllWords, getAgeFromDateString, isValidUUID } from "../../utils/helpers";
import { deepCopyObject } from "../../utils/helpers";
import UserDialog from "../dialogs/userDialog";
import OmniAvatar from "../ui/omniAvatar";
import OmniEditButton from "../ui/omniEditButton";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import Widget from "../ui/widget";
import WidgetList from "../ui/widgetList";
import WidgetSkeleton from "../ui/widgetSkeleton";
import WidgetTitle from "../ui/widgetTitle";

const PracticePatients = props => {
  const { relatedPatientODMs } = props;

  const [selectedPatient, setSelectedPatient] = useState(null);

  const getPatientInitials = patientODM => {
    let theReturn =
      patientODM.omniData.givenName.charAt(0) + patientODM.omniData.familyName.charAt(0);

    return theReturn.toUpperCase();
  };

  const getPatientDescription = patientODM => {
    let theReturn = "";
    if (patientODM.omniData.patientData.race != null) {
      theReturn += capitalizeAllWords(patientODM.omniData.patientData.race);
    }

    if (patientODM.omniData.patientData.ethnicity != null) {
      if (theReturn.length > 0) {
        theReturn += "  ";
      }
      theReturn += capitalizeAllWords(patientODM.omniData.patientData.ethnicity);
    }
    if (patientODM.omniData.patientData.gender != null) {
      if (theReturn.length > 0) {
        theReturn += "  ";
      }
      theReturn += capitalizeAllWords(patientODM.omniData.patientData.gender);
    }

    if (theReturn.length > 0) {
      theReturn += ",  ";
    }
    theReturn += `Age ${getAgeFromDateString(patientODM.omniData.patientData.birthdate)}`;

    return theReturn;
  };

  const loadPatientImage = patientId => {
    try {
      //TODO call DB here to get data retrieval
      let image = `${
        window.location.origin.includes("localhost")
          ? `https://dev.omnisciahealth.coach`
          : window.location.origin
      }/assets/demo/images/userImage(3).jpg`;

      if (patientId === constants.demoUserId.userTwo) {
        image = `${
          window.location.origin.includes("localhost")
            ? `https://dev.omnisciahealth.coach`
            : window.location.origin
        }/assets/demo/images/userImage(1).jpg`;
      }
      if (patientId === constants.demoUserId.userThree) {
        image = `${
          window.location.origin.includes("localhost")
            ? `https://dev.omnisciahealth.coach`
            : window.location.origin
        }/assets/demo/images/userImage(22).jpg`;
      }
      if (patientId === constants.demoUserId.userFour) {
        image = `${
          window.location.origin.includes("localhost")
            ? `https://dev.omnisciahealth.coach`
            : window.location.origin
        }/assets/demo/images/userImage(11).jpg`;
      }
      return image;
    } catch (error) {
      return `${
        window.location.origin.includes("localhost")
          ? `https://dev.omnisciahealth.coach`
          : window.location.origin
      }/assets/demo/images/userImage(3).jpg`;
    }
  };

  const navigateToManagePortal = () => {
    if (props.awsGroup === constants.userGroups.Creator) {
      props.history.push(constants.appPaths.appPathCreator);
    }
    if (props.awsGroup === constants.userGroups.Administrator) {
      props.history.push(constants.appPaths.appPathAdminPatient);
    }
  };

  const [titleText, setTitleText] = useState(null);

  useEffect(() => {
    if (relatedPatientODMs != null) {
      setTitleText(
        `${Object.entries(relatedPatientODMs).length} Customer${
          relatedPatientODMs.length === 1 ? "" : "s"
        }`,
      );
    } else setTitleText("");
  }, [relatedPatientODMs]);

  const theme = useTheme();

  return (
    <Widget>
      <WidgetTitle title={"Patients"} text={titleText} loading={relatedPatientODMs == null}>
        <OmniResponsiveButton
          tooltipTitle="Add a patient to this practice"
          buttonText="Patient"
          onClick={() => {
            setSelectedPatient(createNewUser(true));
          }}
        />
      </WidgetTitle>

      {relatedPatientODMs == null && <WidgetSkeleton />}
      {relatedPatientODMs != null && (
        <WidgetList>
          {relatedPatientODMs
            .sort((a, b) => a.omniData.familyName.localeCompare(b.omniData.familyName))
            .map((patient, index) => {
              return (
                <ListItem
                  key={`patient${index}`}
                  disableGutters={true}
                  sx={{
                    padding: theme.spacing(1),
                    paddingTop: 0,
                    paddingBottom: 0,
                    marginBottom: theme.spacing(1),
                    borderRadius: theme.spacing(1),
                    backgroundColor: alpha(theme.palette.info.main, 0.75),
                    color: theme.palette.info.contrastText,
                    cursor: "pointer",
                    boxShadow: theme.shadows[3],
                    userSelect: "none",
                    webkitUserSelect: "none",
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedPatient(null);
                    setSelectedPatient(patient);
                  }}
                >
                  {!isValidUUID(patient._id) && (
                    <Tooltip title={"No Notes"} placement="right">
                      <OmniAvatar
                        sx={{
                          width: 36,
                          height: 36,
                          fontSize: "1.8rem",
                          border: `2px solid ${theme.palette.info.contrastText}`,
                        }}
                        src={loadPatientImage(patient._id)}
                      ></OmniAvatar>
                    </Tooltip>
                  )}

                  {isValidUUID(patient._id) && (
                    <Tooltip title={"No Notes"} placement="right">
                      <OmniAvatar
                        sx={{
                          width: 36,
                          height: 36,
                          fontSize: "1.2rem",
                          color: theme.palette.info.main,
                          border: `2px solid ${theme.palette.info.contrastText}`,
                        }}
                      >
                        {getPatientInitials(patient)}
                      </OmniAvatar>
                    </Tooltip>
                  )}
                  <ListItemText
                    primary={
                      <Typography
                        variant="h6"
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          marginLeft: theme.spacing(1),
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color: theme.palette.info.contrastText,
                          marginTop: 0,
                        }}
                      >
                        {patient.omniData.displayName
                          ? patient.omniData.displayName
                          : `${patient.omniData.givenName} ${patient.omniData.familyName}`}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          marginLeft: theme.spacing(1),
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          marginTop: "-6px",
                          color: theme.palette.info.contrastText,
                          minimumHeight: 20,
                        }}
                      >
                        {getPatientDescription(patient)}
                      </Typography>
                    }
                  />
                  <OmniEditButton
                    onClick={e => {
                      e.stopPropagation();
                      props.loadPatientODMRequest(patient._id);
                      navigateToManagePortal();
                    }}
                    title={"Manage"}
                    icon={
                      <OpenInNewIcon
                        sx={{
                          height: 42,
                          width: 42,
                        }}
                      />
                    }
                  />
                </ListItem>
              );
            })}
        </WidgetList>
      )}

      {selectedPatient && (
        <UserDialog
          open={selectedPatient != null}
          dialogTitle={`${selectedPatient._id == null ? "Add" : "Edit"} Patient`}
          omniODM={deepCopyObject(selectedPatient)}
          onDelete={patient => {
            props.deletePracticePatientRequest(patient._id);
            setSelectedPatient(null);
          }}
          onAccept={patient => {
            props.updatePracticePatientRequest(patient);
            setSelectedPatient(null);
          }}
          onClose={() => {
            setSelectedPatient(null);
          }}
        />
      )}
    </Widget>
  );
};
const mapStateToProps = state => ({
  awsGroup: state.userSession.awsGroup,
  relatedPatientODMs: state.practiceSession.relatedPatientODMs,
});

const mapDispatchToProps = {
  deletePracticePatientRequest: PracticeSessionActions.deletePracticePatientRequest,
  updatePracticePatientRequest: PracticeSessionActions.updatePracticePatientRequest,
  loadPatientODMRequest: PatientSessionActions.loadPatientODMRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(PracticePatients));
